<template>
  <v-container class="h-full">
    <h1 class="mb-2">
      User Edit Page
    </h1>

    <v-card color="darknavyblue" elevation="5" class="pa-4 mb-4">
      <v-card-title>User Details</v-card-title>
      <v-card-text>
        <v-progress-circular v-if="!details" indeterminate />
        <template v-else>
          <p><strong>User ID:</strong> <span v-text="details.id" /></p>
          <p><strong>Email:</strong> <span v-text="details.email" /></p>
          <p><strong>First Name:</strong> <span v-text="details.firstName" /></p>
          <p><strong>Last Name:</strong> <span v-text="details.lastName" /></p>
          <p><strong>Account Type:</strong> <span v-text="prettyType(details.type)" /></p>
          <p><strong>Onboarded:</strong> <span v-text="details.onboarded ? 'Yes' : 'No'" /></p>
        </template>
      </v-card-text>
    </v-card>

    <v-card color="darknavyblue" elevation="5" class="pa-4 mb-4">
      <v-card-title>Programs</v-card-title>
      <v-card-text>
        <v-progress-circular v-if="membershipsLoading" indeterminate />
        <template v-else>
          <v-row>
            <v-col cols="6">
              <h2>Member of</h2>
              <v-progress-circular v-if="!accessibleGroups" indeterminate class="mt-2" />
              <p v-else-if="!accessibleGroups.length" class="text-error">
                No groups in current Environment
              </p>
              <v-list v-else bg-color="transparent">
                <v-list-item
                  v-for="group of accessibleGroups"
                  :key="group.groupId"
                  :title="group.program.name"
                  :subtitle="group.name"
                  class="py-2"
                  border
                >
                  <template #append>
                    <v-chip
                      variant="flat"
                      density="compact"
                      color="success"
                      v-text="prettyRoles.get(group.role)"
                    />
                    <v-btn
                      v-if="!adminRoles.includes(group.role)"
                      color="error"
                      icon="mdi-minus"
                      size="24"
                      class="ml-2"
                      :disabled="removeLoading"
                      :loading="removeLoading"
                      @click="removeFromProgram(group)"
                    />
                  </template>
                </v-list-item>
              </v-list>
              <div v-if="removeLoading" class="d-flex justify-end align-center text-error mt-2 mr-2">
                <p class="mr-2">
                  Removing Program...
                </p>
                <v-progress-circular indeterminate />
              </div>
            </v-col>
            <v-col cols="6">
              <h2 class="mb-2">
                Add To New Program
              </h2>
              <v-select
                v-model="newAccess.program"
                label="Select Program"
                :items="scopedPrograms"
                item-value="id"
                item-title="name"
                return-object
                clearable
                :loading="programsLoading"
                no-data-text="No programs enrolled in the current Environment + Domain"
              >
                <template #item="{ internalItem: item, props }">
                  <v-list-item v-bind="props" :title="item.raw.name" :subtitle="item.raw.client" />
                </template>
              </v-select>
              <v-select
                v-model="newAccess.group"
                label="Select Group"
                :items="availableGroups"
                item-title="name"
                item-value="id"
                return-object
                clearable
                :disabled="!newAccess.program || programGroupsLoading"
                :loading="programGroupsLoading"
              />
              <v-select
                v-model="newAccess.role"
                label="Select Role"
                :items="availableRoles"
                item-title="name"
                item-value="role"
                clearable
                :disabled="!newAccess.group"
                no-data-text="No roles available for the current Domain"
              />
              <v-btn
                color="primary"
                block
                :loading="addLoading"
                :disabled="addDisabled"
                @click="addToProgram(newAccess.program!, newAccess.group!, newAccess.role!)"
              >
                Add Access
              </v-btn>
            </v-col>
          </v-row>
        </template>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script lang="ts">
import { UserType, Role } from '@policyfly/protobuf/pfbingo'
import { ADMIN_ROLES } from '@policyfly/utils/constants'
import { storeToRefs } from 'pinia'
import { computed, defineComponent, onMounted, reactive, ref, shallowRef, watch } from 'vue'
import { useRoute } from 'vue-router'

import { useApiStore } from '@/stores/api'
import { useDomainStore } from '@/stores/domain'
import { useEnvironmentStore } from '@/stores/environment'
import { useProgramStore } from '@/stores/program'

import { prettyRoles, prettyGroupRelationshipTypes, programRolesForDomain } from '@/utils/program'

import type {
  User,
  Program,
  Domain,
  GroupRelationshipType,
  ReadUserMembershipsResponse_ProgramContext,
  ReadUserMembershipsResponse_ProgramContext_Group,
  Group,
  ListGroupsForProgramResponse_Group,
} from '@policyfly/protobuf/pfbingo'

interface GroupWithProgram extends ReadUserMembershipsResponse_ProgramContext_Group {
  program: Program
  role: Role
}

interface ProgramGroupOption {
  id: string
  name: string
  relationshipType: GroupRelationshipType
  group: Group
}

export default defineComponent({
  name: 'UserEdit',

  setup () {
    const apiStore = useApiStore()
    const domainStore = useDomainStore()
    const environmentStore = useEnvironmentStore()
    const route = useRoute()
    const userId = computed(() => route.params.id as string)

    // details
    const details = shallowRef<User | null>(null)
    function prettyType (type: UserType): string {
      switch (type) {
        case UserType.USER_TYPE_HUMAN: return 'Human'
        case UserType.USER_TYPE_SERVICE_ACCOUNT: return 'Service Account'
        case UserType.USER_TYPE_BOOTSTRAP: return 'Bootstrap'
        case UserType.USER_TYPE_SSO: return 'SSO'
        default: return 'Unknown'
      }
    }
    onMounted(async () => {
      const { users } = await apiStore.loadUserDetails({
        ids: [userId.value],
      })
      details.value = users[0] ?? null
    })

    // memberships
    const membershipsLoading = ref(true)
    const userMemberships = shallowRef<ReadUserMembershipsResponse_ProgramContext[] | null>(null)
    async function loadUserMemberships (environmentId: string, domain: Domain): Promise<void> {
      membershipsLoading.value = true
      const { memberships } = await apiStore.loadUserMemberships({
        userId: userId.value,
        environmentId,
        domain,
      })
      userMemberships.value = Object.values(memberships)
      membershipsLoading.value = false
    }
    // pending access grant
    const newAccess = reactive({
      program: null as Program | null,
      group: null as ProgramGroupOption | null,
      role: null as Role | null,
    })

    // programs available to grant access to are scoped to the current environment + domain
    const programStore = useProgramStore()
    const { scopedPrograms, scopedProgramsLoading: programsLoading } = storeToRefs(programStore)

    watch(() => ({
      domain: domainStore.currentDomain,
      environment: environmentStore.currentEnvironment,
    }), async ({ domain, environment }) => {
      // a program selected in another environment/domain is not valid in this one
      newAccess.program = null
      if (!environment?.id) return
      await Promise.all([
        loadUserMemberships(environment.id, domain),
        programStore.loadScopedPrograms(),
      ])
    }, { immediate: true })

    // groups
    const programGroups = shallowRef<ListGroupsForProgramResponse_Group[]>([])
    const programGroupsLoading = ref(false)

    const addLoading = ref(false)
    const addDisabled = computed(() => {
      return !newAccess.program || !newAccess.group || !newAccess.role || programGroupsLoading.value || addLoading.value
    })
    async function addToProgram (program: Program, group: ProgramGroupOption, role: Role): Promise<void> {
      const answer = window.confirm(`Are you sure you want to grant access to the ${program.name} program?`)
      if (!answer) return
      addLoading.value = true
      const environmentId = environmentStore.currentEnvironment!.id
      const domain = domainStore.currentDomain
      try {
        await apiStore.addToGroup({
          userId: userId.value,
          groupId: group.group.id,
          defaultRole: role,
          environmentId,
          admin: false,
          domain,
        })
        await apiStore.addToProgram({
          environmentId,
          programId: program.id,
          groupId: group.group.id,
          additions: [{ userId: userId.value, role }],
          domain,
        })
        await loadUserMemberships(environmentId, domain)
        newAccess.group = null
        newAccess.role = null
      } finally {
        addLoading.value = false
      }
    }

    const removeLoading = ref(false)
    async function removeFromProgram (group: GroupWithProgram): Promise<void> {
      const answer = window.confirm(`Are you sure you want to remove access to the ${group.program.name} program?`)
      if (!answer) return
      removeLoading.value = true
      const environmentId = environmentStore.currentEnvironment!.id
      const domain = domainStore.currentDomain
      try {
        await apiStore.removeFromProgram({
          environmentId,
          programId: group.program.id,
          groupId: group.groupId,
          removals: [{ userId: userId.value }],
          domain,
        })
        await apiStore.removeUserFromGroup({
          environmentId,
          userId: userId.value,
          groupId: group.groupId,
          domain,
        })
        await loadUserMemberships(environmentId, domain)
      } finally {
        removeLoading.value = false
      }
    }

    const availableRoles = computed(() => {
      if (!newAccess.group) return []
      return programRolesForDomain(domainStore.currentDomain, newAccess.group.relationshipType)
        .map((role) => ({ role, name: prettyRoles.get(role) }))
    })

    async function loadProgramGroups (programId: string, environmentId: string, domain = domainStore.currentDomain): Promise<void> {
      programGroupsLoading.value = true
      try {
        const response = await apiStore.listGroupsForProgram({
          programId,
          environmentId,
          domain,
        })
        programGroups.value = response.groups
      } finally {
        programGroupsLoading.value = false
      }
    }
    watch(() => ({
      domain: domainStore.currentDomain,
      environment: environmentStore.currentEnvironment,
      program: newAccess.program,
    }), async ({ domain, environment, program }) => {
      newAccess.group = null
      newAccess.role = null
      if (!program?.id || !environment?.id) {
        programGroups.value = []
        return
      }
      await loadProgramGroups(program.id, environment.id, domain)
    })
    watch(() => newAccess.group, () => {
      newAccess.role = null
    })

    const accessibleGroups = computed(() => {
      return userMemberships.value
        ?.flatMap<GroupWithProgram>((m) => {
          return m.groups
            .map((g) => ({
              ...g,
              program: m.program!,
              role: m.role,
            }))
        })
    })
    // filter out groups the user is already a member of
    const availableGroups = computed(() => {
      if (!newAccess.program) return []
      const memberGroupIds = new Set(
        accessibleGroups.value?.map((g) => g.groupId) ?? [],
      )
      return programGroups.value
        .filter((g) => g.group && !memberGroupIds.has(g.group.id))
        .map((g) => ({
          id: g.group!.id,
          name: g.group!.name,
          relationshipType: g.relationshipType,
          group: g.group!,
        }))
        .sort((a, b) => a.name.localeCompare(b.name))
    })
    function prettyRelationshipType (relationshipType: GroupRelationshipType): string {
      return prettyGroupRelationshipTypes.get(relationshipType) || 'Unknown'
    }

    return {
      // details
      details,
      prettyRelationshipType,
      prettyRoles,
      prettyType,
      // memberships
      membershipsLoading,
      // programs
      Role,
      adminRoles: ADMIN_ROLES,
      scopedPrograms,
      programsLoading,
      // groups
      accessibleGroups,
      availableGroups,
      availableRoles,
      programGroupsLoading,
      // access
      newAccess,
      addLoading,
      addDisabled,
      addToProgram,
      removeLoading,
      removeFromProgram,
    }
  },
})
</script>
